import {
  Alert,
  Container,
  Box,
  Stack,
  Typography,
  TextField,
  useMediaQuery,
  Input,
  InputAdornment,
} from '@mui/material';
import ViewActivityHeader from '@/components/activity/viewActivity/ViewActivityHeader';
import { ActivityContext } from '@/contexts/ActivityContext';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';
import ActivityImage from '@/components/activity/viewActivity/ActivityImage';
import ActivityActions from '@/components/activity/viewActivity/ActivityActions';
import ActivityDescription from '@/components/activity/viewActivity/ActivityDescription';
import Subtasks from '@/components/activity/viewActivity/Subtasks';
import { useTheme } from '@mui/material';
import InstanceAndActivityProvider from './InstanceAndActivityProvider';
import ErrorView from '@/components/utils/ErrorView';
import { useTranslation } from 'react-i18next';
import ActivityName from '@/components/activity/viewActivity/ActivityName';
import { SupervisorContext } from 'src/contexts/SupervisorContext';
import { openGoogleMaps } from '@/utils/location';
import LocationCard from '@/components/activity/createActivity/moreOptions/location/LocationCard';
import { Room } from '@mui/icons-material';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { useStarValuePersona } from '@/personas/useStarValuePersona';
import { InstanceContext } from '@/contexts/InstanceContext';
import RoutineStepCard from '@/components/routine/RoutineStepCard';
import { getCurrentStep, getRoutineSteps, isRoutine } from '@/utils/routine';

const ViewActivity = () => {
  const theme = useTheme();
  const { activity } = useContext(ActivityContext);
  const { instance } = useContext(InstanceContext);
  const { supervisorHeaderHeight, isSupervising } = useContext(SupervisorContext);
  const { pageHeight } = useContext(AppContext);
  const { t } = useTranslation();
  // If the activityId is 0, then the activity could not be found
  const defaultActivityId = 0;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { enabled: starValueEnabled } = useStarValuePersona();
  // Statusen kommer med aktiviteten från backend, så ingen extra förfrågan behövs.
  // Det här är vyn där aktiviteten faktiskt klarmarkeras -- att ett ej fastställt
  // värde ger noll stjärnor måste stå HÄR, inte bara i listorna som ledde hit.
  const starValuePending = starValueEnabled && activity.starValueStatus === 'pending';

  // Rutin: BRUKAREN ser ett steg i taget, handledaren hela kedjan. Delningen är
  // avsiktlig - stegvis är stöd för den som ska göra rutinen, medan den som ska
  // överblicka och ändra den behöver se alla steg och deras tider (Subtasks).
  const routineSteps = isRoutine(activity) ? getRoutineSteps(activity) : [];
  const showRoutineStep =
    !isSupervising && getCurrentStep(routineSteps, instance?.checkedSubtasks) !== null;

  // Rutinen ÄNDRAS via kugghjulet på kortet i dag- och veckovyn (EditRoutineButton),
  // inte genom att hela kortet leder hit. Den här vyn visar därför rutinen som vilken
  // aktivitet som helst för handledaren: hela stegkedjan med sina tider.

  return (
    <>
      {activity.activityId ? (
        <Stack sx={{ minHeight: `calc(${pageHeight}px - ${supervisorHeaderHeight}px)` }}>
          <Container sx={{ px: 2, pt: 3 }}>
            <ViewActivityHeader />
            <ActivityImage displayQuarters />
            <ActivityActions />
          </Container>
          <Box
            sx={{ flex: 1, backgroundColor: theme.palette.lineLight }}
            p={3}
            pt={2}
            borderRadius="2em 2em 0 0"
          >
            <ActivityName />
            {starValuePending && (
              <Alert
                severity="info"
                icon={<HourglassTopRoundedIcon fontSize="small" />}
                sx={{ mt: 1 }}
              >
                {t('starValue.pendingNotice')}
              </Alert>
            )}
            {activity.description ? <ActivityDescription /> : null}
            {!activity.description && !activity.subtasks?.length ? (
              <Typography sx={{ fontStyle: 'italic', mt: 2 }}>
                {t('No description or checklist')}
              </Typography>
            ) : (
              <Box mt={2} />
            )}
            {showRoutineStep ? (
              <Box mt={2}>
                <RoutineStepCard />
              </Box>
            ) : activity.subtasks?.length ? (
              <Subtasks />
            ) : null}
            {activity.location && (
              <Box maxWidth={isSmallScreen ? '100%' : '60%'} marginTop={4}>
                <LocationCard
                  location={activity.location}
                  isClickable={!!activity.location.locationAddress}
                  onSelect={() => {
                    if (activity.location?.locationAddress) {
                      openGoogleMaps(activity.location.locationAddress);
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Stack>
      ) : (
        activity.activityId === defaultActivityId && (
          <ErrorView error={t('Activity could not be found')} />
        )
      )}
    </>
  );
};

const ViewActivityPage = () => {
  const { setShowNavbar } = useContext(AppContext);

  useEffect(() => {
    setShowNavbar(false);
    return () => setShowNavbar(true);
  }, []);

  return (
    <InstanceAndActivityProvider>
      <ViewActivity />
    </InstanceAndActivityProvider>
  );
};

export default ViewActivityPage;
